import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, ChevronRight, Gift, Home, LogIn, LogOut,
  Share2, TrendingUp, User, UserPlus, Wallet, X, ArrowDownToLine, ArrowUpFromLine,
} from "lucide-react";
import API from "../services/api";
import WhatsAppGlyph from "./WhatsAppGlyph";
import { buildWhatsAppUrl, openExternalLink, shareAppLink } from "../utils/appInteractions";

function Sidebar({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [settings, setSettings] = useState({ whatsapp_number: "" });

  useEffect(() => {
    API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {});
  }, []);

  const close = () => setIsOpen(false);
  const go = (path, protect = false) => { close(); navigate(protect && !user ? "/login" : path); };
  const logout = () => { localStorage.removeItem("user"); localStorage.removeItem("posm_session_active"); close(); navigate("/login"); };

  const openWhatsapp = () => {
    const phone = getPhone(settings.whatsapp_number);
    if (!phone) return;
    const url = buildWhatsAppUrl(phone, "Hello, I need support");
    if (url) openExternalLink(url);
  };

  const shareApp = async () => {
    const code = String(user?.referral_code || "").trim();
    const url = `${window.location.origin}/login?tab=register${code ? `&ref=${encodeURIComponent(code)}` : ""}`;
    try { const shared = await shareAppLink({ title: "Play Online Satta Matka", text: code ? `Join with my code ${code}` : "Join Play Online Satta Matka", url }); if (!shared) alert("Link copied!"); } catch (_) {}
  };

  const waPhone = String(settings.whatsapp_number || "").replace(/\D/g, "").slice(-10);

  return (
    <>
      {isOpen && <div onClick={close} style={overlay} />}
      <aside style={{ ...drawer, transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}>

        {/* Header */}
        <div style={drawerHead}>
          <div style={headLeft}>
            <div style={headLogo}>🎲</div>
            <div>
              <div style={headTitle}><span style={{ color: "#f97316" }}>Play Online</span><span style={{ color: "#4ade80" }}> Matka</span></div>
              <div style={headSub}>India's Trusted Platform</div>
            </div>
          </div>
          <button type="button" style={closeBtn} onClick={close}><X size={16} color="rgba(255,255,255,0.7)" /></button>
        </div>

        {/* User card */}
        {user ? (
          <div style={userCard}>
            <div style={avatarWrap}>
              <div style={avatar}>{String(user.name || "U").charAt(0).toUpperCase()}</div>
              <div style={avatarGlow} />
            </div>
            <div style={userInfo}>
              <div style={userName}>{user.name || "Player"}</div>
              <div style={userPhone}>{user.phone ? `+91 ${String(user.phone).slice(-10)}` : "Member"}</div>
            </div>
            <div style={userBadge}>PRO</div>
          </div>
        ) : (
          <div style={guestCard}>
            <div style={guestIcon}>👋</div>
            <div style={guestText}>Welcome! Login to play &amp; win</div>
            <div style={authRow}>
              <button type="button" style={loginBtn} onClick={() => { close(); navigate("/login"); }}><LogIn size={13} /> Login</button>
              <button type="button" style={regBtn} onClick={() => { close(); navigate("/login?tab=register"); }}><UserPlus size={13} /> Register</button>
            </div>
          </div>
        )}

        {/* Quick action row */}
        {user && (
          <div style={quickRow}>
            <button type="button" style={quickAct} onClick={() => go("/deposit", true)}>
              <ArrowDownToLine size={16} color="#22c55e" />
              <span style={quickActLabel}>Deposit</span>
            </button>
            <div style={quickDivider} />
            <button type="button" style={quickAct} onClick={() => go("/withdraw", true)}>
              <ArrowUpFromLine size={16} color="#f97316" />
              <span style={quickActLabel}>Withdraw</span>
            </button>
            <div style={quickDivider} />
            <button type="button" style={quickAct} onClick={() => go("/wallet", true)}>
              <Wallet size={16} color="#a78bfa" />
              <span style={quickActLabel}>Wallet</span>
            </button>
          </div>
        )}

        {/* Nav */}
        <nav style={nav}>
          <Item icon={Home}        label="Home"          color="#f97316" onClick={() => go("/dashboard")} />
          <Item icon={BarChart3}  label="Charts"        color="#e879f9" onClick={() => go("/charts")} />
          <Item icon={TrendingUp} label="Market Rates"  color="#10b981" onClick={() => go("/game-rates")} />
          <Sep />
          <Item icon={Gift}      label="Refer &amp; Earn"  color="#fbbf24" onClick={() => go("/refer", true)} />
          <Item icon={User}      label="My Profile"   color="#a78bfa" onClick={() => go("/profile", true)} />
          {user && <>
            <Sep />
            <Item icon={Share2}  label="Share App"    color="#38bdf8" onClick={shareApp} />
            <Item icon={LogOut}  label="Logout"       color="#f87171" onClick={logout} />
          </>}
        </nav>

        {/* WhatsApp support */}
        <div style={foot}>
          <button type="button" style={waBtn} onClick={openWhatsapp}>
            <div style={waLeft}>
              <div style={waIconBox}><WhatsAppGlyph size={18} color="#fff" /></div>
              <div style={waInfo}>
                <span style={waTitle}>WhatsApp Support</span>
                <span style={waSub}>{waPhone ? `+91 ${waPhone}` : "Tap to chat"}</span>
              </div>
            </div>
            <ChevronRight size={16} color="rgba(255,255,255,0.5)" />
          </button>
        </div>

      </aside>
    </>
  );
}

function Item({ icon: Icon, label, color, onClick }) {
  return (
    <button type="button" style={navItem} onClick={onClick}>
      <span style={{ ...iconWrap, background: `${color}22`, border: `1px solid ${color}44` }}>
        <Icon size={16} color={color} />
      </span>
      <span style={navLabel}>{label}</span>
      <ChevronRight size={13} color="rgba(255,255,255,0.25)" />
    </button>
  );
}
function Sep() { return <div style={sep} />; }

function getPhone(val) {
  const d = String(val || "").replace(/\D/g, "");
  if (d.length === 10) return `91${d}`;
  if (d.length === 12 && d.startsWith("91")) return d;
  return d;
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 999, backdropFilter: "blur(4px)" };
const drawer  = { position: "fixed", inset: "0 auto 0 0", width: 280, maxWidth: "88vw", height: "100dvh", background: "linear-gradient(180deg,#13111f 0%,#1c1a2e 50%,#0f1a0f 100%)", zIndex: 1000, transition: "transform 0.28s cubic-bezier(0.16,1,0.3,1)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "10px 0 50px rgba(0,0,0,0.5)" };

const drawerHead  = { padding: "14px 14px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 };
const headLeft    = { display: "flex", alignItems: "center", gap: 10 };
const headLogo    = { fontSize: 28, lineHeight: 1 };
const headTitle   = { fontSize: 14, fontWeight: 900, letterSpacing: "-0.01em" };
const headSub     = { fontSize: 10, color: "rgba(255,255,255,0.4)", fontWeight: 600, marginTop: 2 };
const closeBtn    = { width: 32, height: 32, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 9, background: "rgba(255,255,255,0.05)", display: "grid", placeItems: "center", cursor: "pointer" };

const userCard  = { margin: "12px 12px 4px", padding: "12px", borderRadius: 16, background: "linear-gradient(135deg,rgba(249,115,22,0.18),rgba(124,58,237,0.12))", border: "1px solid rgba(249,115,22,0.25)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, position: "relative", overflow: "hidden" };
const avatarWrap= { position: "relative", flexShrink: 0 };
const avatar    = { width: 42, height: 42, borderRadius: "50%", background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", fontWeight: 900, fontSize: 18, display: "grid", placeItems: "center" };
const avatarGlow= { position: "absolute", inset: -3, borderRadius: "50%", border: "2px solid rgba(249,115,22,0.5)", animation: "none" };
const userInfo  = { minWidth: 0, flex: 1 };
const userName  = { color: "#fff", fontWeight: 800, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const userPhone = { color: "rgba(255,255,255,0.45)", fontSize: 11, marginTop: 2 };
const userBadge = { fontSize: 9, fontWeight: 900, color: "#f97316", background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)", borderRadius: 6, padding: "2px 6px", letterSpacing: "0.05em" };

const guestCard = { margin: "12px 12px 4px", padding: "16px", borderRadius: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", flexShrink: 0, textAlign: "center" };
const guestIcon = { fontSize: 28, marginBottom: 6 };
const guestText = { color: "rgba(255,255,255,0.55)", fontSize: 12, fontWeight: 600, marginBottom: 12 };
const authRow   = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 };
const loginBtn  = { height: 36, border: 0, borderRadius: 10, background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontWeight: 800, fontSize: 12, cursor: "pointer" };
const regBtn    = { ...loginBtn, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" };

const quickRow     = { margin: "8px 12px", borderRadius: 14, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", flexShrink: 0 };
const quickAct     = { flex: 1, height: 52, border: 0, background: "transparent", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, cursor: "pointer" };
const quickActLabel= { fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.6)" };
const quickDivider = { width: 1, height: 28, background: "rgba(255,255,255,0.1)", flexShrink: 0 };

const nav     = { flex: 1, overflowY: "auto", minHeight: 0, padding: "6px 10px", display: "grid", alignContent: "start", gap: 2 };
const navItem = { height: 48, display: "grid", gridTemplateColumns: "36px 1fr 16px", alignItems: "center", gap: 2, border: 0, background: "transparent", textAlign: "left", borderRadius: 12, padding: "0 8px", cursor: "pointer" };
const iconWrap= { width: 32, height: 32, display: "grid", placeItems: "center", borderRadius: 9 };
const navLabel= { fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.9)", paddingLeft: 4 };
const sep     = { height: 1, background: "rgba(255,255,255,0.07)", margin: "4px 2px" };

const foot    = { padding: "10px 10px calc(12px + env(safe-area-inset-bottom, 0px))", borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 };
const waBtn   = { width: "100%", border: 0, borderRadius: 14, background: "linear-gradient(135deg,#075E54,#25D366)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "11px 12px", cursor: "pointer", boxShadow: "0 4px 16px rgba(37,211,102,0.3)" };
const waLeft  = { display: "flex", alignItems: "center", gap: 10 };
const waIconBox={ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.18)", display: "grid", placeItems: "center", flexShrink: 0 };
const waInfo  = { display: "flex", flexDirection: "column", gap: 2, textAlign: "left" };
const waTitle = { fontSize: 13, fontWeight: 900, color: "#fff" };
const waSub   = { fontSize: 10, color: "rgba(255,255,255,0.7)", fontWeight: 600 };

export default Sidebar;
